import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, DarkModeToggleCompact } from '../../components/ui'
import { ArrowLeft, BookOpen, Users, Trophy, Activity, HelpCircle, ChevronRight, CheckCircle, Zap, Target, Crown, Monitor, Palette, QrCode, FileText, ExternalLink, MessageCircle, RefreshCw, Gamepad2, Coins, CreditCard } from 'lucide-react'
import { askGemini, PROPOULE_SYSTEM_PROMPT } from '../../lib/gemini'
import { useTranslation } from 'react-i18next'

export const HelpPage = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [selectedSport, setSelectedSport] = useState(null) // 'ai', 'racket', 'team', 'individual', 'live'

    const renderSportContent = () => {
        switch (selectedSport) {
            case 'ai':
                return <AIChatAssistant />
            case 'live':
                return (
                    <div className="animate-slideUp space-y-8">
                        <div className="bg-gradient-to-r from-orange-50 to-orange-100 border-l-4 border-orange-500 p-6 rounded-r-xl shadow-sm">
                            <h3 className="text-2xl font-bold text-orange-900 mb-2 flex items-center gap-2">
                                <Monitor className="text-orange-600" /> {t('help.section_live_tv')}
                            </h3>
                            <p className="text-orange-800">
                                {t('help.section_live_tv_text')}
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                            {/* LIVE LINK & QR */}
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-orange-100 hover:shadow-md transition-shadow">
                                <h4 className="font-bold text-lg mb-4 flex items-center gap-2 text-indigo-700">
                                    <QrCode size={20} /> {t('help.paper_to_digital')}
                                </h4>
                                <p className="text-sm text-gray-600 mb-4">
                                    {t('help.paper_to_digital_text')}
                                </p>
                                <ul className="space-y-3 text-sm bg-indigo-50/50 p-4 rounded-xl border border-indigo-100">
                                    <li className="flex items-start gap-2">
                                        <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                                        <span><strong>{t('help.label_public_link')}</strong> {t('help.desc_public_link')}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                                        <span><strong>{t('help.label_qr_code')}</strong> {t('help.desc_qr_code')}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                                        <span><strong>{t('help.label_my_team')}</strong> {t('help.desc_my_team')}</span>
                                    </li>
                                </ul>
                            </div>

                            {/* STYLING & UPLOADS */}
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-orange-100 hover:shadow-md transition-shadow">
                                <h4 className="font-bold text-lg mb-4 flex items-center gap-2 text-pink-700">
                                    <Palette size={20} /> {t('help.styling_title')}
                                </h4>
                                <div className="text-sm text-gray-600 space-y-3">
                                    <p>{t('help.styling_desc')}</p>
                                    <div className="bg-pink-50 border border-pink-100 p-3 rounded-lg text-pink-700 font-medium">
                                        <strong>{t('help.label_navigation')}</strong> <span dangerouslySetInnerHTML={{ __html: t('help.desc_navigation') }} />
                                    </div>
                                    <p>
                                        {t('help.styling_extra')}
                                        <br />
                                        {t('help.upload_warning')}
                                    </p>
                                    <div className="flex flex-wrap gap-2 mt-4">
                                        <div className="text-[10px] uppercase font-bold tracking-wide bg-gray-100 px-2 py-1 rounded border">{t('help.label_bg')}</div>
                                        <div className="text-[10px] uppercase font-bold tracking-wide bg-gray-100 px-2 py-1 rounded border">{t('help.label_banners')}</div>
                                    </div>
                                </div>
                            </div>

                            {/* TV MODUS */}
                            <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-2xl shadow-sm border border-purple-100 col-span-full">
                                <h4 className="font-bold text-lg mb-4 flex items-center gap-2 text-purple-700">
                                    <Monitor size={20} /> {t('help.tv_mode_title')}
                                </h4>
                                <p className="text-sm text-gray-600 mb-6">
                                    {t('help.tv_mode_desc')}
                                </p>
                                <div className="grid md:grid-cols-3 gap-4 text-sm">
                                    <div className="p-4 bg-white rounded-xl border border-purple-100 shadow-sm relative overflow-hidden group">
                                        <div className="absolute right-0 top-0 p-2 opacity-10 group-hover:opacity-20 transition-opacity"><Monitor size={40} /></div>
                                        <strong className="text-purple-700 block mb-1">{t('help.step_setup')}</strong>
                                        {t('help.step_setup_desc')}
                                    </div>
                                    <div className="p-4 bg-white rounded-xl border border-purple-100 shadow-sm relative overflow-hidden group">
                                        <div className="absolute right-0 top-0 p-2 opacity-10 group-hover:opacity-20 transition-opacity"><CheckCircle size={40} /></div>
                                        <strong className="text-purple-700 block mb-1">{t('help.step_start')}</strong>
                                        {t('help.step_start_desc')}
                                    </div>
                                    <div className="p-4 bg-white rounded-xl border border-purple-100 shadow-sm relative overflow-hidden group">
                                        <div className="absolute right-0 top-0 p-2 opacity-10 group-hover:opacity-20 transition-opacity"><Zap size={40} /></div>
                                        <strong className="text-purple-700 block mb-1">{t('help.step_auto')}</strong>
                                        {t('help.step_auto_desc')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            case 'racket':
                return (
                    <div className="animate-slideUp space-y-8">
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 p-6 rounded-r-xl shadow-sm">
                            <h3 className="text-2xl font-bold text-green-900 mb-2 flex items-center gap-2">
                                <Activity className="text-green-600" /> {t('help.racket_full_title')}
                            </h3>
                            <p className="text-green-800">
                                {t('help.racket_full_desc')}
                            </p>
                        </div>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                                <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                                    <span className="bg-green-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs shadow-md">1</span>
                                    {t('help.categories_title')}
                                </h4>
                                <p className="text-sm text-gray-600 mb-4">
                                    {t('help.categories_desc')}
                                </p>
                                <ul className="space-y-2 text-sm bg-gray-50 p-3 rounded-lg border border-gray-100">
                                    <li className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500" /> {t('help.cat_hd4')}</li>
                                    <li className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500" /> {t('help.cat_de6')}</li>
                                </ul>
                            </div>
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                                <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                                    <span className="bg-green-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs shadow-md">2</span>
                                    {t('help.scheduling_title')}
                                </h4>
                                <p className="text-sm text-gray-600 mb-4">
                                    {t('help.scheduling_desc')}
                                </p>
                                <div className="space-y-3">
                                    <div className="flex gap-3 items-start p-3 bg-blue-50 text-blue-800 rounded-lg border border-blue-100">
                                        <Zap className="text-blue-600 shrink-0 mt-1" size={16} />
                                        <div className="text-sm">
                                            <strong>{t('help.label_auto_schema')}</strong> <span dangerouslySetInnerHTML={{ __html: t('help.desc_auto_schema') }} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            case 'team':
                return (
                    <div className="animate-slideUp space-y-8">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-6 rounded-r-xl shadow-sm">
                            <h3 className="text-2xl font-bold text-blue-900 mb-2 flex items-center gap-2">
                                <Users className="text-blue-600" /> {t('help.teams_full_title')}
                            </h3>
                            <p className="text-blue-800">
                                {t('help.teams_full_desc')}
                            </p>
                        </div>
                        <div className="relative border-l-2 border-blue-200 ml-4 space-y-8 pb-4">
                            <div className="pl-8 relative group">
                                <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-blue-500 border-4 border-white shadow group-hover:scale-125 transition-transform"></div>
                                <h4 className="font-bold text-lg text-gray-900 mb-1">{t('help.step_indeling_title')}</h4>
                                <p className="text-sm text-gray-600">
                                    {t('help.step_indeling_desc')}
                                </p>
                            </div>
                            <div className="pl-8 relative group">
                                <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-blue-500 border-4 border-white shadow group-hover:scale-125 transition-transform"></div>
                                <h4 className="font-bold text-lg text-gray-900 mb-1">{t('help.step_schema_title')}</h4>
                                <p className="text-sm text-gray-600 mb-2">
                                    {t('help.step_schema_desc')}
                                </p>
                            </div>
                            <div className="pl-8 relative group">
                                <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-blue-500 border-4 border-white shadow group-hover:scale-125 transition-transform"></div>
                                <h4 className="font-bold text-lg text-gray-900 mb-1">{t('help.step_knockout_title')}</h4>
                                <p className="text-sm text-gray-600">
                                    <span dangerouslySetInnerHTML={{ __html: t('help.step_knockout_desc') }} />
                                </p>
                            </div>
                        </div>
                    </div>
                )
            case 'individual':
                return (
                    <div className="animate-slideUp space-y-8">
                        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-500 p-6 rounded-r-xl shadow-sm">
                            <h3 className="text-2xl font-bold text-purple-900 mb-2 flex items-center gap-2">
                                <Target className="text-purple-600" /> {t('help.individual_full_title')}
                            </h3>
                            <p className="text-purple-800">
                                {t('help.individual_full_desc')}
                            </p>
                        </div>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="bg-white p-6 rounded-2xl shadow-sm border-t-4 border-indigo-500 hover:shadow-lg transition-all hover:-translate-y-1">
                                <div className="flex justify-between items-start mb-4">
                                    <h4 className="font-bold text-lg">{t('help.ladder_comp')}</h4>
                                    <div className="bg-indigo-100 p-2 rounded-lg text-indigo-600"><Activity size={20} /></div>
                                </div>
                                <ul className="space-y-2 text-sm text-gray-600">
                                    <li className="flex items-center gap-2"><CheckCircle size={14} className="text-indigo-500" /> {t('help.winner_up_loser_down')}</li>
                                    <li className="flex items-center gap-2"><CheckCircle size={14} className="text-indigo-500" /> {t('help.perfect_ongoing')}</li>
                                </ul>
                            </div>
                            <div className="bg-white p-6 rounded-2xl shadow-sm border-t-4 border-yellow-500 hover:shadow-lg transition-all hover:-translate-y-1">
                                <div className="flex justify-between items-start mb-4">
                                    <h4 className="font-bold text-lg">{t('help.king_comp')}</h4>
                                    <div className="bg-yellow-100 p-2 rounded-lg text-yellow-600"><Crown size={20} /></div>
                                </div>
                                <p className="text-sm text-gray-600 mb-4">
                                    {t('help.king_desc')}
                                </p>
                                <div className="text-xs bg-yellow-50 text-yellow-800 p-2 rounded border border-yellow-100">
                                    {t('help.king_main_court')}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            case 'legal':
                return (
                    <div className="animate-slideUp space-y-8">
                        <div className="bg-gray-50 border-l-4 border-gray-500 p-6 rounded-r-xl">
                            <h3 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                                <FileText className="text-gray-600" /> {t('help.legal_full_title')}
                            </h3>
                            <p className="text-gray-800">
                                {t('help.legal_full_desc')}
                            </p>
                        </div>
                        <div className="grid md:grid-cols-2 gap-6">
                            <a href="/terms" target="_blank" className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-lg hover:border-gray-300 transition-all group">
                                <h4 className="font-bold text-lg mb-2 flex items-center gap-2 text-gray-900">
                                    {t('help.terms_title')} <ExternalLink size={16} className="text-gray-400 group-hover:text-orange-500" />
                                </h4>
                                <p className="text-sm text-gray-600">
                                    {t('help.terms_desc')}
                                </p>
                            </a>
                            <a href="/privacy" target="_blank" className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-lg hover:border-gray-300 transition-all group">
                                <h4 className="font-bold text-lg mb-2 flex items-center gap-2 text-gray-900">
                                    {t('help.privacy_title')} <ExternalLink size={16} className="text-gray-400 group-hover:text-orange-500" />
                                </h4>
                                <p className="text-sm text-gray-600">
                                    {t('help.privacy_desc')}
                                </p>
                            </a>
                        </div>
                    </div>
                )
            case 'esports':
                return (
                    <div className="animate-slideUp space-y-8">
                        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-l-4 border-purple-500 p-6 rounded-r-xl shadow-sm">
                            <h3 className="text-2xl font-bold text-purple-900 mb-2 flex items-center gap-2">
                                <Gamepad2 className="text-purple-600" /> {t('help.esports_full_title')}
                            </h3>
                            <p className="text-purple-800">
                                {t('help.esports_full_desc')}
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                            {/* HOE HET WERKT (BEHEERDER) */}
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-purple-100 hover:shadow-md transition-shadow">
                                <h4 className="font-bold text-lg mb-4 flex items-center gap-2 text-purple-700">
                                    <Zap size={20} /> {t('help.esports_admin_title')}
                                </h4>
                                <p className="text-sm text-gray-600 mb-4">
                                    {t('help.esports_admin_desc')}
                                </p>
                                <ul className="space-y-3 text-sm bg-purple-50/50 p-4 rounded-xl border border-purple-100 text-gray-700">
                                    <li className="flex items-start gap-2">
                                        <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                                        <span><strong>{t('help.esports_step1_title')}</strong> {t('help.esports_step1_desc')}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                                        <span><strong>{t('help.esports_step2_title')}</strong> {t('help.esports_step2_desc')}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                                        <span><strong>{t('help.esports_step3_title')}</strong> {t('help.esports_step3_desc')}</span>
                                    </li>
                                </ul>
                            </div>

                            {/* HOE HET WERKT (SPELERS) */}
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-purple-100 hover:shadow-md transition-shadow">
                                <h4 className="font-bold text-lg mb-4 flex items-center gap-2 text-cyan-700">
                                    <Target size={20} /> {t('help.esports_player_title')}
                                </h4>
                                <p className="text-sm text-gray-600 mb-4">
                                    {t('help.esports_player_desc')}
                                </p>
                                <ul className="space-y-3 text-sm bg-cyan-50/50 p-4 rounded-xl border border-cyan-100 text-gray-700">
                                    <li className="flex items-start gap-2">
                                        <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                                        <span><strong>{t('help.esports_pstep1_title')}</strong> {t('help.esports_pstep1_desc')}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                                        <span><strong>{t('help.esports_pstep2_title')}</strong> {t('help.esports_pstep2_desc')}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                                        <span><strong>{t('help.esports_pstep3_title')}</strong> {t('help.esports_pstep3_desc')}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )
            case 'monetization':
                return (
                    <div className="animate-slideUp space-y-8">
                        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-emerald-500 p-6 rounded-r-xl shadow-sm">
                            <h3 className="text-2xl font-bold text-emerald-950 mb-2 flex items-center gap-2">
                                <Coins className="text-emerald-600 animate-pulse" /> {t('help.monetization_full_title', { defaultValue: 'Geld & Sponsoring Handleiding' })}
                            </h3>
                            <p className="text-emerald-800 text-sm">
                                {t('help.monetization_full_desc', { defaultValue: 'Leer hoe je inschrijfgelden int voor toernooien en hoe premium sponsoren prominent en realtime in beeld worden gebracht op schermen en mobiele telefoons.' })}
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                            {/* BETAALDE INSCHRIJVINGEN */}
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-emerald-100 hover:shadow-md transition-shadow">
                                <h4 className="font-bold text-lg mb-4 flex items-center gap-2 text-emerald-800">
                                    <CreditCard size={20} className="text-emerald-600" /> {t('help.payments_title', { defaultValue: 'Inschrijfgelden innen' })}
                                </h4>
                                <p className="text-sm text-gray-600 mb-4">
                                    {t('help.payments_desc', { defaultValue: 'Je kunt teams direct online laten betalen bij de inschrijving om achterstallige betalingen en handmatige administratie te voorkomen.' })}
                                </p>
                                <ul className="space-y-3 text-sm bg-emerald-50/40 p-4 rounded-xl border border-emerald-100 text-gray-700">
                                    <li className="flex items-start gap-2">
                                        <CheckCircle size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                                        <span><strong>{t('help.pay_step1', { defaultValue: 'Stap 1: Activeren' })}</strong> {t('help.pay_step1_desc', { defaultValue: 'Ga naar Toernooi-instellingen en vink "Betaalde inschrijvingen activeren" aan.' })}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                                        <span><strong>{t('help.pay_step2', { defaultValue: 'Stap 2: Mollie API-sleutel' })}</strong> {t('help.pay_step2_desc', { defaultValue: 'Vul je Mollie API-sleutel in (live of test). Selecteer iDEAL, Bancontact of Creditcard.' })}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                                        <span><strong>{t('help.pay_step3', { defaultValue: 'Stap 3: Tarief' })}</strong> {t('help.pay_step3_desc', { defaultValue: 'Bepaal de inschrijfkosten per team. Teams betalen dit veilig bij inschrijving.' })}</span>
                                    </li>
                                </ul>
                            </div>

                            {/* PREMIUM SPONSOREN */}
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-emerald-100 hover:shadow-md transition-shadow">
                                <h4 className="font-bold text-lg mb-4 flex items-center gap-2 text-indigo-800">
                                    <Trophy size={20} className="text-indigo-600" /> {t('help.sponsors_title', { defaultValue: 'Premium Sponsoring' })}
                                </h4>
                                <p className="text-sm text-gray-600 mb-4">
                                    {t('help.sponsors_desc', { defaultValue: 'Verhoog de opbrengsten van je toernooi door sponsoren een prominente, geautomatiseerde plek te geven.' })}
                                </p>
                                <ul className="space-y-3 text-sm bg-indigo-50/40 p-4 rounded-xl border border-indigo-100 text-gray-700">
                                    <li className="flex items-start gap-2">
                                        <CheckCircle size={16} className="text-indigo-600 mt-0.5 shrink-0" />
                                        <span><strong>{t('help.spon_step1', { defaultValue: 'Sponsoren Hub' })}</strong> {t('help.spon_step1_desc', { defaultValue: 'Voeg logo\'s toe via het menu "Sponsoren". Bepaal optioneel een externe link naar de sponsorsite.' })}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle size={16} className="text-indigo-600 mt-0.5 shrink-0" />
                                        <span><strong>{t('help.spon_step2', { defaultValue: 'TV Kantine Slideshow' })}</strong> {t('help.spon_step2_desc', { defaultValue: 'Sponsorlogo\'s roteren automatisch mee als grote, full-screen dia\'s in de TV Modus.' })}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle size={16} className="text-indigo-600 mt-0.5 shrink-0" />
                                        <span><strong>{t('help.spon_step3', { defaultValue: 'Mobiele Live-pagina' })}</strong> {t('help.spon_step3_desc', { defaultValue: 'Sponsoren roteren interactief als een elegante footer onderaan het live-schema op mobiel.' })}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )
            default:
                return null
        }
    }

    return (
        <div className="min-h-screen">
            <div className="premium-container">
                <div className="mb-8 flex justify-between items-start animate-slideUp">
                    <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="bg-white/50 backdrop-blur-sm">
                        <ArrowLeft size={16} className="mr-2" /> {t('help.btn_back')}
                    </Button>
                    <DarkModeToggleCompact />
                </div>

                <div className="flex flex-col items-center mb-12 animate-slideUp">
                    <div className="p-3 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-xl mb-4 text-white">
                        <BookOpen size={32} />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black text-gray-900 text-center tracking-tight mb-2">
                        {t('help.title')}
                    </h1>
                    <p className="text-gray-500 text-center max-w-xl">
                        {t('help.subtitle')}
                    </p>
                </div>

                {!selectedSport ? (
                    <div className="space-y-8 animate-scaleIn">
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {/* AI BUTTON - NOW POWERED BY REAL AI */}
                            <button onClick={() => setSelectedSport('ai')} className="group bg-gradient-to-br from-purple-600 to-indigo-600 p-6 rounded-3xl shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all border border-transparent text-left relative overflow-hidden text-white">
                                <div className="absolute -top-4 -right-4 bg-white/10 w-32 h-32 rounded-full blur-2xl"></div>
                                <div className="absolute top-0 right-0 p-4 opacity-20">
                                    <MessageCircle size={80} className="stroke-[1.5]" />
                                </div>
                                <div className="w-12 h-12 bg-white/20 text-white rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform backdrop-blur-sm border border-white/20">
                                    <Zap size={24} className="fill-white" />
                                </div>
                                <h3 className="text-xl font-bold mb-1">{t('help.ai_title')}</h3>
                                <p className="text-purple-100 mb-6 text-xs leading-relaxed opacity-90">{t('help.ai_desc')}</p>
                                <span className="text-white font-bold text-xs flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full w-fit hover:bg-white/30 transition-colors">{t('help.ai_btn')} <ChevronRight size={14} /></span>
                            </button>

                            {/* Other categories stay the same */}
                            <button onClick={() => setSelectedSport('live')} className="group bg-white hover:bg-orange-50 p-6 rounded-3xl shadow-sm hover:shadow-xl hover:scale-[1.02] transition-all border border-gray-100 hover:border-orange-200 text-left relative">
                                <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                    <Monitor size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-orange-700">{t('help.live_title')}</h3>
                                <p className="text-gray-500 mb-6 text-xs leading-relaxed">{t('help.live_desc')}</p>
                                <span className="text-orange-600 font-bold text-xs flex items-center gap-1 group-hover:gap-2 transition-all">{t('help.live_btn')} <ChevronRight size={14} /></span>
                            </button>

                            <button onClick={() => setSelectedSport('racket')} className="group bg-white hover:bg-green-50 p-6 rounded-3xl shadow-sm hover:shadow-xl hover:scale-[1.02] transition-all border border-gray-100 hover:border-green-200 text-left relative">
                                <div className="w-12 h-12 bg-green-100 text-green-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                    <Activity size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-green-700">{t('help.racket_title')}</h3>
                                <p className="text-gray-500 mb-6 text-xs leading-relaxed">{t('help.racket_desc')}</p>
                                <span className="text-green-600 font-bold text-xs flex items-center gap-1 group-hover:gap-2 transition-all">{t('help.live_btn')} <ChevronRight size={14} /></span>
                            </button>

                            <button onClick={() => setSelectedSport('team')} className="group bg-white hover:bg-blue-50 p-6 rounded-3xl shadow-sm hover:shadow-xl hover:scale-[1.02] transition-all border border-gray-100 hover:border-blue-200 text-left relative">
                                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                    <Users size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-blue-700">{t('help.team_title')}</h3>
                                <p className="text-gray-500 mb-6 text-xs leading-relaxed">{t('help.team_desc')}</p>
                                <span className="text-blue-600 font-bold text-xs flex items-center gap-1 group-hover:gap-2 transition-all">{t('help.live_btn')} <ChevronRight size={14} /></span>
                            </button>

                            <button onClick={() => setSelectedSport('esports')} className="group bg-white hover:bg-purple-50 p-6 rounded-3xl shadow-sm hover:shadow-xl hover:scale-[1.02] transition-all border border-gray-100 hover:border-purple-200 text-left relative">
                                <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                    <Gamepad2 size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-purple-700">{t('help.esports_title')}</h3>
                                <p className="text-gray-500 mb-6 text-xs leading-relaxed">{t('help.esports_desc')}</p>
                            </button>

                            <button onClick={() => setSelectedSport('monetization')} className="group bg-white hover:bg-emerald-50 p-6 rounded-3xl shadow-sm hover:shadow-xl hover:scale-[1.02] transition-all border border-gray-100 hover:border-emerald-200 text-left relative">
                                <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                    <Coins size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-emerald-700">{t('help.monetization_title', { defaultValue: 'Geld & Sponsoren' })}</h3>
                                <p className="text-gray-500 mb-6 text-xs leading-relaxed">{t('help.monetization_desc', { defaultValue: 'Richt inschrijfgelden via Mollie in en beheer premium toernooisponsoren.' })}</p>
                                <span className="text-emerald-600 font-bold text-xs flex items-center gap-1 group-hover:gap-2 transition-all">{t('help.live_btn')} <ChevronRight size={14} /></span>
                            </button>

                            {/* Additional small cards */}
                            <button onClick={() => setSelectedSport('individual')} className="group bg-white hover:bg-purple-50 p-6 rounded-3xl shadow-sm hover:shadow-lg transition-all border border-gray-100 hover:border-purple-200 text-left lg:col-span-2 flex items-center gap-4">
                                <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-2xl flex-shrink-0 flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <Target size={24} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-purple-700">{t('help.individual_title')}</h3>
                                    <p className="text-xs text-gray-500">{t('help.individual_desc')}</p>
                                </div>
                                <div className="ml-auto text-purple-400 group-hover:text-purple-600"><ChevronRight /></div>
                            </button>

                            <button onClick={() => setSelectedSport('legal')} className="group bg-white hover:bg-gray-50 p-6 rounded-3xl shadow-sm hover:shadow-lg transition-all border border-gray-100 hover:border-gray-200 text-left lg:col-span-2 flex items-center gap-4">
                                <div className="w-12 h-12 bg-gray-100 text-gray-600 rounded-2xl flex-shrink-0 flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <FileText size={24} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-gray-700">{t('help.legal_title')}</h3>
                                    <p className="text-xs text-gray-500">{t('help.legal_desc')}</p>
                                </div>
                                <div className="ml-auto text-gray-400 group-hover:text-gray-600"><ChevronRight /></div>
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="glass-strong premium-card p-8 rounded-3xl md:p-12 animate-slideUp">
                        <button onClick={() => setSelectedSport(null)} className="mb-8 text-gray-400 hover:text-gray-800 font-bold text-sm flex items-center gap-2 transition-colors uppercase tracking-wide group">
                            <div className="p-1 bg-gray-100 rounded-full group-hover:bg-gray-200"><ArrowLeft size={14} /></div> {t('help.btn_back_overview')}
                        </button>
                        {renderSportContent()}
                    </div>
                )}
            </div>
        </div>
    )
}

// --- REAL AI CHAT ASSISTANT COMPONENT ---
const AIChatAssistant = () => {
    const { t, i18n } = useTranslation()
    const [query, setQuery] = useState('')
    const [response, setResponse] = useState(null)
    const [isThinking, setIsThinking] = useState(false)
    const [history, setHistory] = useState([]) // Voor toekomstige multi-turn chat

    const handleSearch = async (e) => {
        if (e) e.preventDefault()
        if (!query.trim()) return

        setIsThinking(true)
        setResponse(null)

        try {
            // Combine System Prompt + Query
            const lang = t('common.language_name', { defaultValue: i18n?.language === 'nl' ? 'Nederlands' : 'English' })
            const fullPrompt = `${PROPOULE_SYSTEM_PROMPT}\n\nUser Question: ${query}\nAnswer in ${lang}.`;

            const aiAnswer = await askGemini(fullPrompt);

            setResponse({ type: 'success', text: aiAnswer });

            // Keep history (simple implementation)
            setHistory([...history, { role: 'user', content: query }, { role: 'assistant', content: aiAnswer }]);

        } catch (err) {
            setResponse({
                type: 'error',
                text: t('help.chat_error')
            });
        } finally {
            setIsThinking(false)
        }
    }

    return (
        <div className="max-w-2xl mx-auto">
            <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-3xl p-8 text-white shadow-2xl mb-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>

                <div className="flex items-center gap-6 mb-8 relative z-10">
                    <div className="bg-white/20 p-4 rounded-2xl backdrop-blur-md shadow-inner border border-white/20">
                        <Zap size={32} className="text-yellow-300 fill-yellow-300" />
                    </div>
                    <div>
                        <h2 className="text-3xl font-bold mb-1">{t('help.chat_title')}</h2>
                        <p className="text-purple-200">{t('help.chat_subtitle')}</p>
                    </div>
                </div>

                <form onSubmit={handleSearch} className="relative z-10">
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t('help.chat_placeholder')}
                        className="w-full bg-white/10 border border-white/20 rounded-2xl px-6 py-5 text-white placeholder-purple-200 focus:outline-none focus:bg-white/20 focus:border-white/40 transition-all pr-14 shadow-inner text-lg font-medium"
                        autoFocus
                    />
                    <button
                        type="submit"
                        disabled={!query || isThinking}
                        className="absolute right-3 top-3 bottom-3 bg-white text-purple-600 p-3 rounded-xl font-bold hover:bg-purple-50 disabled:opacity-50 transition-colors shadow-lg"
                    >
                        {isThinking ? <RefreshCw className="animate-spin" /> : <ChevronRight />}
                    </button>
                </form>
            </div>

            {isThinking && (
                <div className="flex justify-center py-12">
                    <span className="flex gap-2">
                        <span className="w-3 h-3 bg-[var(--primary)] rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                        <span className="w-3 h-3 bg-[var(--primary)] rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                        <span className="w-3 h-3 bg-[var(--primary)] rounded-full animate-bounce"></span>
                    </span>
                </div>
            )}

            {response && (
                <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-xl animate-slideUp relative">
                    <div className="absolute -top-3 left-8 w-6 h-6 bg-white rotate-45 border-t border-l border-gray-100"></div>
                    <div className="flex gap-6">
                        <div className={`p-4 rounded-2xl h-fit shrink-0 ${response.type === 'error' ? 'bg-red-50 text-red-500' : 'bg-green-50 text-green-600'}`}>
                            {response.type === 'error' ? <HelpCircle size={28} /> : <CheckCircle size={28} />}
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 mb-3 text-lg">
                                {response.type === 'error' ? t('help.chat_label_error') : t('help.chat_label_gemini')}
                            </h3>
                            <div className="text-gray-700 leading-relaxed text-lg prose">
                                {/* Simple renderer for now, AI returns markdown usually */}
                                {response.text.split('\n').map((line, i) => <p key={i} className="mb-2">{line}</p>)}
                            </div>

                            <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-400 italic">
                                {t('help.chat_disclaimer')}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-16 text-center">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-6">{t('help.suggestions_title')}</h3>
                <div className="flex flex-wrap justify-center gap-3">
                    {[
                        t('help.sugg_schema'), 
                        t('help.sugg_king'), 
                        t('help.sugg_refs'),
                        t('help.sugg_payments', { defaultValue: 'Hoe stel ik Mollie betalingen in?' }),
                        t('help.sugg_sponsors', { defaultValue: 'Hoe werken sponsoren op het TV-scherm?' })
                    ].map((q, i) => (
                        <button
                            key={i}
                            onClick={() => { setQuery(q); handleSearch(null) }}
                            className="bg-white border border-gray-200 px-5 py-2.5 rounded-full text-sm font-medium text-gray-600 hover:border-purple-300 hover:text-purple-600 hover:bg-purple-50 transition-all shadow-sm hover:shadow-md"
                        >
                            {q}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    )
}
